/**
 * Admin-local endpoint slice for `/api/v1/notifications*`
 * (apps/api-worker/src/routes/notifications.ts).
 *
 * Wrapped endpoints:
 *
 *   GET  /notifications               (query: unreadOnly, limit, offset)
 *   GET  /notifications/unread-count
 *   POST /notifications/read-all
 *   POST /notifications/:id/read
 *   POST /notifications               (send — permission `notification.send`)
 *
 * The inbox reads are self-scoped server-side (`d.user_id = caller`) and need
 * `notification.view.self`. The `/messages*` direct-chat endpoints of the same
 * router are deliberately NOT wrapped — no Admin screen uses them yet.
 *
 * A row in the inbox is one DELIVERY (the per-recipient fan-out row), so
 * mark-read takes the `deliveryId`, not the notification id.
 */
import type { RequestOptions } from '@sonauto/api-client';
import { apiClient } from '../api.js';

type Extra = Pick<RequestOptions, 'signal' | 'reason' | 'idempotencyKey' | 'timeoutMs'>;

function extra(o: Extra | undefined): RequestOptions {
  return o === undefined ? {} : { ...o };
}

// ---------------------------------------------------------------------------
// Types — mirror routes/notifications.ts exactly
// ---------------------------------------------------------------------------

export const NOTIFICATION_CATEGORIES = [
  'TASK',
  'ORDER',
  'KITCHEN',
  'ALERT',
  'HR',
  'PAYROLL',
  'SYSTEM',
  'CUSTOMER',
  'TRANSLATION',
  'APPROVAL',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number];

export const NOTIFICATION_AUDIENCE_TYPES = ['USER', 'ROLE', 'BRANCH', 'STATION'] as const;
export type NotificationAudienceType = (typeof NOTIFICATION_AUDIENCE_TYPES)[number];

export interface NotificationItem {
  readonly deliveryId: string;
  readonly notificationId: string;
  /** `notification_type`, e.g. `manual.announcement` or `DIRECT_MESSAGE`. */
  readonly type: string;
  readonly category: string;
  readonly priority: string;
  readonly title: string;
  readonly body: string | null;
  readonly deepLink: string | null;
  readonly resourceType: string | null;
  readonly resourceId: string | null;
  readonly fromUserId: string | null;
  /** Delivery status: QUEUED | SENT | DELIVERED | READ | … */
  readonly status: string;
  readonly readAt: number | null;
  readonly deliveredAt: number | null;
  readonly createdAt: number;
}

export interface NotificationListResponse {
  readonly notifications: readonly NotificationItem[];
  readonly limit: number;
  readonly offset: number;
}

export interface UnreadCountResponse {
  readonly unread: number;
}

export interface ReadAllResponse {
  readonly ok: true;
  readonly marked: number;
}

export interface MarkReadResponse {
  readonly ok: true;
  readonly deliveryId: string;
  readonly alreadyRead: boolean;
  readonly readAt: number | null;
}

export interface NotificationListQuery {
  readonly unreadOnly?: boolean;
  /** 1..200, default 50. */
  readonly limit?: number;
  readonly offset?: number;
}

/** Mirrors the server's `sendSchema` (zod) field for field. */
export interface SendNotificationInput {
  readonly audienceType: NotificationAudienceType;
  readonly audienceRef: string;
  readonly branchId?: string | null;
  readonly title: string;
  readonly body?: string | null;
  readonly category?: NotificationCategory;
  readonly priority?: NotificationPriority;
  readonly notificationType?: string;
  readonly deepLink?: string | null;
  readonly resourceType?: string | null;
  readonly resourceId?: string | null;
  readonly requiresAck?: boolean;
}

export interface SendNotificationResponse {
  readonly ok: true;
  readonly notificationId: string;
  readonly recipients: number;
  /** True when the audience exceeded the 500-recipient fan-out cap. */
  readonly capped: boolean;
}

// ---------------------------------------------------------------------------
// Wrappers
// ---------------------------------------------------------------------------

export const notificationsApi = {
  /** The caller's own inbox, unread first. Requires `notification.view.self`. */
  list: (query: NotificationListQuery = {}, o?: Extra): Promise<NotificationListResponse> =>
    apiClient.get<NotificationListResponse>('/v1/notifications', { ...extra(o), query: { ...query } }),

  unreadCount: (o?: Extra): Promise<UnreadCountResponse> =>
    apiClient.get<UnreadCountResponse>('/v1/notifications/unread-count', extra(o)),

  readAll: (o?: Extra): Promise<ReadAllResponse> =>
    apiClient.post<ReadAllResponse>('/v1/notifications/read-all', extra(o)),

  markRead: (deliveryId: string, o?: Extra): Promise<MarkReadResponse> =>
    apiClient.post<MarkReadResponse>(
      `/v1/notifications/${encodeURIComponent(deliveryId)}/read`,
      extra(o),
    ),

  /** Fan a notification out to an audience. Requires `notification.send`. */
  send: (body: SendNotificationInput, o?: Extra): Promise<SendNotificationResponse> =>
    apiClient.post<SendNotificationResponse>('/v1/notifications', { ...extra(o), body }),
};
